import { Component, OnDestroy, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { AbstractCellEditor, CustomCellEditorParams } from '@app/shared/components/cell-edit/abstract.cell-editor';
import {
  FullRowEditCellRendererComponent
} from '@app/shared/components/cell-renderer/full-row-edit-cell-renderer/full-row-edit-cell-renderer.component';

@Component({
  selector: 'percent-cell-edit',
  templateUrl: './percent-cell-edit.component.html',
  styleUrls: ['./percent-cell-edit.component.scss'],
  standalone: false
})
export class PercentCellEditComponent extends AbstractCellEditor implements OnDestroy {
  @ViewChild(MatInput, {static: false}) public input: MatInput;

  /**
   * Prevents typing unwanted characters in a numeric field
   * @param {InputEvent} event
   * @returns {boolean}
   */
  public validateKeypressEvent(event: InputEvent): boolean {
    const input = event.data;
    const pattern = /[0-9\-.,]/;
    return event.inputType != 'insertText' || this.validateInput(input, pattern);
  }

  /**
   * Prevents pasting unwanted characters in a numeric field
   * @param {ClipboardEvent} event
   * @returns {boolean}
   */
  public validatePasteEvent(event: ClipboardEvent): boolean {
    const input = event.clipboardData.getData('text');
    const pattern = /^(?:-?\d+|-?\d{1,3}(?:\s?\d{3})+)?(?:[,.]\d+)?$/;
    return this.validateInput(input, pattern);
  }

  /**
   * Validates a string against a given pattern
   *
   * @param input The string to validate
   * @param pattern The regular expression pattern for validation
   * @return True if the string is valid, false otherwise
   */
  private validateInput(input: string, pattern: RegExp): boolean {
    return pattern.test(input);
  }

  /**
   * Initialisation method for the render component
   * @param params A merge of default cell editor params and custom params
   */
  public agInit(params: CustomCellEditorParams): void {
    super.agInit(params);
  }

  /**
   *  A hook to perform any necessary operation just after the GUI for this component has been rendered on the screen.
   *  This method is called each time the edit component is activated.
   */
  public afterGuiAttached(): void {
    if (!this.fullRowEdit) {
      this.input.focus();
    }
  }

  /**
   * Gets called when focus should be put into the editor.
   */
  public focusIn(): void {
    this.input.focus();
  }

  /**
   * Gets called when focus is leaving the editor.
   */
  public focusOut(): void {
    if (!this.fullRowEdit) {
      return;
    }

    // Focus on the button row if it's the last editable cell on full row edit.
    const lastEditableCellColumnId = this.params.api.getColumns().lastOrNull((column) => {
      return column.isCellEditable(this.params.node);
    }).getId();

    if (this.params.column.getId() === lastEditableCellColumnId) {
      const fullRowEditCellRenderer = this.params.api.getCellRendererInstances().find((cellRenderer) => {
        return cellRenderer instanceof FullRowEditCellRendererComponent && cellRenderer.params.node.rowIndex == this.params.rowIndex;
      }) as FullRowEditCellRendererComponent;
      fullRowEditCellRenderer.focusIn();
    }
  }

  /**
   * Return the final value - called by the grid once after editing is complete
   */
  public getValue(): string {
    return this.control.value;
  }

  public ngOnDestroy(): void {
    this.params.eGridCell.classList.remove('pending-cell');
    this.params.eGridCell.classList.remove('error-cell');
  }

  /**
   * Update tooltip message in case of validation error
   * @param type Type of the validator in error
   * @param definition Definition of the validator in error
   */
  protected setTooltip(type: string, definition: string | number): void {
    this.errorTooltip = this.translateService.instant(
      `ERROR.FIELD_${type}`.toUpperCase(),
      {value: definition}
    );
  }
}
